/**
 * FinancialPaymentLinks - Página de Links de Pagamento
 * 
 * Layout premium padronizado com MetricCards, filtros inline e tabela
 * Renderização condicional de dialogs para performance otimizada
 * 
 * @author Módulo Financeiro
 * @version 2.0.0
 * RELEASE: 2026-03-21 v1.0.0 - Criação da página
 * RELEASE: 2026-03-22 v2.0.0 - Layout premium padronizado, performance otimizada
 */

import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useFinancialPaymentLinks, type PaymentLink } from '@/hooks/useFinancialPaymentLinks';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, Link as LinkIcon, RefreshCw, Search, Eye, EyeOff,
  MoreHorizontal, Pencil, Power, PowerOff, RotateCcw, Trash2,
  Copy, ExternalLink, Loader2, CheckCircle, XCircle,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const logger = createLogger('FinancialPaymentLinks');

// ============================================================
// TIPOS
// ============================================================

/** Conta corrente com provider configurado */
interface ContaCorrente {
  id: string;
  nome: string;
  provider_slug: string | null;
}

// ============================================================
// CONSTANTES
// ============================================================

/** Labels para billing_type */
const BILLING_TYPE_LABELS: Record<string, string> = {
  UNDEFINED: 'Todos',
  BOLETO: 'Boleto',
  CREDIT_CARD: 'Cartão',
  PIX: 'PIX',
};

/** Labels para charge_type */
const CHARGE_TYPE_LABELS: Record<string, string> = {
  DETACHED: 'Avulsa',
  INSTALLMENT: 'Parcelada',
};

// ============================================================
// COMPONENTES AUXILIARES - Padrão Premium
// ============================================================

/** Estilos semânticos para MetricCard - padrão horizontal compacto */
const METRIC_CARD_STYLES = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Props do MetricCard */
interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  variant: keyof typeof METRIC_CARD_STYLES;
}

/**
 * Card de métrica horizontal compacto — padrão unificado
 */
function MetricCard({ title, value, icon: Icon, variant }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

export default function FinancialPaymentLinks() {
  const { user } = useAuth();

  // Estado: conta corrente selecionada
  const [contaCorrenteId, setContaCorrenteId] = useState<string>('');
  const [contas, setContas] = useState<ContaCorrente[]>([]);
  const [loadingContas, setLoadingContas] = useState(true);

  // Estado: dialogs (renderização condicional para performance)
  const [formOpen, setFormOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<PaymentLink | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLink, setDetailLink] = useState<PaymentLink | null>(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'deleted'>('all');

  // Hook de dados
  const {
    links, isLoading, refetch,
    createLink, isCreating, updateLink, isUpdating,
    deleteLink, restoreLink, toggleActive,
    uploadImage, isUploadingImage, deleteImage, isDeletingImage, setMainImage,
  } = useFinancialPaymentLinks(contaCorrenteId);

  // ============================================================
  // CARREGAR CONTAS CORRENTES
  // ============================================================
  useEffect(() => {
    async function loadContas() {
      try {
        const { data, error } = await supabase
          .from('financeiro_conta_corrente' as any)
          .select('id, nome, provider_slug')
          .not('provider_slug', 'is', null);

        if (error) throw error;

        const contasData = (data || []) as unknown as ContaCorrente[];
        setContas(contasData);

        // Auto-selecionar primeira conta se houver apenas uma
        if (contasData.length === 1) {
          setContaCorrenteId(contasData[0].id);
        }
      } catch (err) {
        logger.error('Erro ao carregar contas correntes', err);
      } finally {
        setLoadingContas(false);
      }
    }

    if (user) loadContas();
  }, [user]);

  // ============================================================
  // MÉTRICAS - calculadas via useMemo
  // ============================================================
  const stats = useMemo(() => {
    const activeCount = links.filter(l => l.active && !l.deleted).length;
    const inactiveCount = links.filter(l => !l.active && !l.deleted).length;
    const totalViews = links.reduce((sum, l) => sum + (l.view_count || 0), 0);
    return {
      total: links.length,
      active: activeCount,
      inactive: inactiveCount,
      views: totalViews,
    };
  }, [links]);

  // ============================================================
  // DADOS FILTRADOS
  // ============================================================
  const filteredLinks = useMemo(() => {
    return links.filter((link) => {
      // Filtro de busca
      if (search && !link.name.toLowerCase().includes(search.toLowerCase())) return false;
      // Filtro de status
      if (statusFilter === 'active') return link.active && !link.deleted;
      if (statusFilter === 'inactive') return !link.active && !link.deleted;
      if (statusFilter === 'deleted') return link.deleted;
      return true;
    });
  }, [links, search, statusFilter]);

  // ============================================================
  // HANDLERS
  // ============================================================

  /** Abre dialog para edição */
  const handleEdit = (link: PaymentLink) => {
    setEditingLink(link);
    setFormOpen(true);
  };

  /** Fecha dialog e limpa estado de edição */
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingLink(null);
  };

  /** Copia URL do link para o clipboard */
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  // ============================================================
  // HOOK: Diálogo de confirmação reutilizável
  // ============================================================
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  /**
   * Handler para remover link de pagamento com validação de cobranças vinculadas.
   * Verifica se existem cobranças antes de permitir a exclusão.
   */
  const handleDeleteLink = async (link: PaymentLink) => {
    try {
      // Consultar cobranças vinculadas ao link
      const { count, error } = await supabase
        .from('financeiro_cobrancas' as any)
        .select('*', { count: 'exact', head: true })
        .eq('provider_payment_link', link.provider_link_id)
        .is('deleted_at', null);

      if (error) {
        logger.error('Erro ao verificar cobranças vinculadas', error);
        toast.error('Erro ao verificar cobranças vinculadas.');
        return;
      }

      const totalCobrancas = count || 0;

      if (totalCobrancas > 0) {
        // Link tem cobranças vinculadas — informar e bloquear exclusão
        await confirmDialog({
          title: 'Não é possível remover',
          description: `Este link possui ${totalCobrancas} cobrança(s) vinculada(s). Para impedir novos pagamentos, desative o link em vez de removê-lo.`,
          variant: 'info',
          confirmText: 'Entendi',
          cancelText: 'Fechar',
          showIcon: true,
        });
        // Não executa exclusão independente da resposta
      } else {
        // Sem cobranças — confirmar exclusão normalmente
        const confirmed = await confirmDialog({
          title: 'Remover link de pagamento?',
          description: 'Esta ação removerá o link permanentemente. Não será possível receber novos pagamentos por ele.',
          variant: 'danger',
          confirmText: 'Remover',
          cancelText: 'Cancelar',
          showIcon: true,
        });

        if (confirmed) {
          await deleteLink({
            paymentLinkId: link.id,
            providerLinkId: link.provider_link_id!,
          });
        }
      }
    } catch (err) {
      logger.error('Erro no handleDeleteLink', err);
      toast.error('Erro ao processar remoção do link.');
    }
  };

  /** Formata valor em BRL */
  const formatCurrency = (value: number | null) => {
    if (value === null) return 'Aberto';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  /** Retorna badge de status */
  const getStatusBadge = (link: PaymentLink) => {
    if (link.deleted) {
      return <Badge variant="destructive" className="text-xs">Removido</Badge>;
    }
    if (link.active) {
      return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/20 text-xs">Ativo</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">Inativo</Badge>;
  };

  return (
    <div className="flex flex-col h-full">
      {/* ============================================================ */}
      {/* HEADER COMPACTO - Padrão premium */}
      {/* ============================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Links de Pagamento</h1>
          <p className="text-xs text-muted-foreground">
            Crie e gerencie links de pagamento permanentes via Asaas
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Refresh */}
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => refetch()}
            disabled={!contaCorrenteId}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {/* Novo Link */}
          <Button
            size="xs"
            onClick={() => setFormOpen(true)}
            disabled={!contaCorrenteId}
            className=""
          >
            <Plus className="mr-1.5" />
            Novo Link
          </Button>
        </div>
      </div>

      {/* ============================================================ */}
      {/* KPI CARDS - Padrão premium */}
      {/* ============================================================ */}
      {contaCorrenteId && !isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
          <MetricCard title="Total de Links" value={stats.total} icon={LinkIcon} variant="primary" />
          <MetricCard title="Ativos" value={stats.active} icon={CheckCircle} variant="success" />
          <MetricCard title="Inativos" value={stats.inactive} icon={XCircle} variant="info" />
          <MetricCard title="Visualizações" value={stats.views} icon={Eye} variant="purple" />
        </div>
      )}

      {/* ============================================================ */}
      {/* BARRA DE FILTROS - Padrão premium */}
      {/* ============================================================ */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Seletor de conta corrente */}
        <Select value={contaCorrenteId} onValueChange={setContaCorrenteId}>
          <SelectTrigger className="w-[220px] h-8 text-xs">
            <SelectValue placeholder="Selecione a conta corrente" />
          </SelectTrigger>
          <SelectContent>
            {contas.map((conta) => (
              <SelectItem key={conta.id} value={conta.id}>
                {conta.nome} ({conta.provider_slug})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtro por status */}
        <div className="flex items-center gap-1">
          {(['all', 'active', 'inactive', 'deleted'] as const).map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="xs"
              onClick={() => setStatusFilter(status)}
              className="h-8"
            >
              {status === 'all' && 'Todos'}
              {status === 'active' && 'Ativos'}
              {status === 'inactive' && 'Inativos'}
              {status === 'deleted' && 'Removidos'}
            </Button>
          ))}
        </div>

        {/* Busca */}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Indicador de carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse ml-auto">
            Atualizando...
          </span>
        )}
      </div>

      {/* ============================================================ */}
      {/* CONTEÚDO - TABELA DE LINKS */}
      {/* ============================================================ */}
      <div className="flex-1 overflow-auto">
        {!contaCorrenteId ? (
          /* Estado vazio: nenhuma conta selecionada */
          <div className="relative border-dashed border-2 border-border/50 rounded-xl bg-gradient-to-br from-muted/30 via-background to-muted/10 p-12 text-center m-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
              <LinkIcon className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Selecione uma conta corrente</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Escolha a conta corrente com provider configurado para gerenciar links
            </p>
          </div>
        ) : isLoading ? (
          /* Loading skeleton */
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (
          <Card className="border-border/50 overflow-hidden bg-gradient-to-br from-background to-muted/10">
            {/* Header da tabela */}
            <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold text-foreground">Lista de Links</h2>
                <Badge variant="secondary" className="font-normal">
                  {filteredLinks.length} encontrado{filteredLinks.length !== 1 ? 's' : ''}
                </Badge>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            </div>

            {/* Tabela ou empty state */}
            {filteredLinks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p className="text-lg font-medium">Nenhum link encontrado</p>
                <p className="text-sm">Crie um novo link de pagamento para começar</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLinks.map((link) => (
                    <TableRow
                      key={link.id}
                      className="cursor-pointer"
                      onClick={() => {
                        setDetailLink(link);
                        setDetailOpen(true);
                      }}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{link.name}</span>
                          {link.description && (
                            <span className="text-xs text-muted-foreground line-clamp-1">{link.description}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(link.value)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {BILLING_TYPE_LABELS[link.billing_type] || link.billing_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {CHARGE_TYPE_LABELS[link.charge_type] || link.charge_type}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusBadge(link)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {link.view_count}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Copiar link */}
                            {link.link_url && (
                              <DropdownMenuItem onClick={() => handleCopyUrl(link.link_url!)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copiar Link
                              </DropdownMenuItem>
                            )}
                            {/* Abrir link */}
                            {link.link_url && (
                              <DropdownMenuItem onClick={() => window.open(link.link_url!, '_blank')}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Abrir Link
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {/* Editar */}
                            {!link.deleted && (
                              <DropdownMenuItem onClick={() => handleEdit(link)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {/* Ativar/Desativar */}
                            {!link.deleted && (
                              <DropdownMenuItem onClick={() => toggleActive({ paymentLinkId: link.id, providerLinkId: link.provider_link_id!, active: !link.active })}>
                                {link.active ? (
                                  <>
                                    <PowerOff className="h-4 w-4 mr-2" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <Power className="h-4 w-4 mr-2" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {/* Remover / Restaurar */}
                            {link.deleted ? (
                              <DropdownMenuItem onClick={() => restoreLink({ paymentLinkId: link.id, providerLinkId: link.provider_link_id! })}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Restaurar
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleDeleteLink(link)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remover
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        )}
      </div>

      {/* ============================================================ */}
      {/* DIALOGS - Renderização condicional para performance */}
      {/* ============================================================ */}
      {formOpen && (
        <PaymentLinkFormDialogLazy
          open={formOpen}
          onClose={handleCloseForm}
          editingLink={editingLink}
          contaCorrenteId={contaCorrenteId}
          onCreate={createLink}
          onUpdate={updateLink}
          isCreating={isCreating}
          isUpdating={isUpdating}
          uploadImage={uploadImage}
          deleteImage={deleteImage}
          setMainImage={setMainImage}
          isUploadingImage={isUploadingImage}
          isDeletingImage={isDeletingImage}
        />
      )}

      {detailOpen && detailLink && (
        <PaymentLinkDetailDialogLazy
          open={detailOpen}
          onClose={() => { setDetailOpen(false); setDetailLink(null); }}
          link={detailLink}
          contaCorrenteId={contaCorrenteId}
        />
      )}

      {/* Dialog de confirmação (useConfirmDialog) */}
      {ConfirmDialogComponent}
    </div>
  );
}

// ============================================================
// IMPORTAÇÕES LAZY DOS DIALOGS (evita carregar no mount)
// ============================================================

import { PaymentLinkFormDialog as PaymentLinkFormDialogLazy } from '@/components/financial/payment-links/PaymentLinkFormDialog';
import { PaymentLinkDetailDialog as PaymentLinkDetailDialogLazy } from '@/components/financial/payment-links/PaymentLinkDetailDialog';
